import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { IChatProps } from './interface'
import { Button, Form, Input } from 'antd'
import { SendOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  ChatWrapper,
  ChatBody,
  ChatFooter,
  MessageRow,
  MessageText,
  MessageDate,
  LoadingPreloader,
} from './styles'

function AppChat({ data, form, onSubmit, onMessageRead }: IChatProps) {
  const [disabledBtn, setDisabledBtn] = useState(true)
  const chatBodyRef = useRef<HTMLDivElement | null>(null)
  const onMessageReadRef = useRef(onMessageRead)
  const hasInitialScrollDoneRef = useRef(false)
  onMessageReadRef.current = onMessageRead

  const handleFormChange = () => {
    const values = form.getFieldsValue()
    setDisabledBtn(!values?.message || values?.message === '')
  }

  const state = useCallback(() => {
    if (data === undefined) return 'loading'
    if (data?.length === 0) return 'empty'
    return 'normal'
  }, [data])

  useLayoutEffect(() => {
    const container = chatBodyRef.current
    if (!container || !data || data.length === 0) return
    if (hasInitialScrollDoneRef.current) return

    const rafId = requestAnimationFrame(() => {
      hasInitialScrollDoneRef.current = true
      const firstUnread = container.querySelector<HTMLElement>('[data-unread-id]')
      if (firstUnread) {
        container.scrollTop = Math.max(0, firstUnread.offsetTop - 8)
      } else {
        container.scrollTop = container.scrollHeight
      }
    })
    return () => cancelAnimationFrame(rafId)
  }, [data])

  useEffect(() => {
    const container = chatBodyRef.current
    const targets = container?.querySelectorAll<HTMLElement>('[data-unread-id]') ?? []
    if (!container || !onMessageReadRef.current || targets.length === 0) return

    let observer: IntersectionObserver | null = null
    const rafId = requestAnimationFrame(() => {
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return
            const id = entry.target.getAttribute('data-unread-id')
            const numId = parseInt(id || '', 10)
            if (numId) onMessageReadRef.current?.(numId)
            observer?.unobserve(entry.target)
          })
        },
        { root: container, rootMargin: '0px', threshold: 0.1 },
      )
      targets.forEach(el => observer?.observe(el))
    })

    return () => {
      cancelAnimationFrame(rafId)
      observer?.disconnect()
    }
  }, [data])

  return (
    <ChatWrapper>
      <ChatBody ref={chatBodyRef} state={state()}>
        {data === undefined && <LoadingPreloader>Загрузка...</LoadingPreloader>}
        {data?.length === 0 && <LoadingPreloader>Нет сообщений</LoadingPreloader>}
        {data &&
          data.length > 0 &&
          [...data].reverse().map(item => {
            const isOutgoing = Boolean(item.manager_id)
            const isIncoming = !isOutgoing

            return (
              <div key={item.id} {...(!item.is_read && { 'data-unread-id': String(item.id) })}>
                <MessageRow $left={isIncoming}>
                  <MessageText $incoming={isIncoming} $is_read={item.is_read}>
                  {item.msg}
                  <MessageDate>{dayjs(item.created_at).format('DD.MM.YYYY HH:mm')}</MessageDate>
                  </MessageText>
                </MessageRow>
              </div>
            )
          })}
      </ChatBody>
      <ChatFooter>
        <Form onFinish={onSubmit} autoComplete="off" onFieldsChange={handleFormChange} form={form}>
          <Form.Item name="message" style={{ marginBottom: 12 }}>
            <Input.TextArea />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button type="primary" htmlType="submit" disabled={disabledBtn}>
              <SendOutlined />
              Отправить
            </Button>
          </Form.Item>
        </Form>
      </ChatFooter>
    </ChatWrapper>
  )
}

AppChat.displayName = 'AppChat'
export default AppChat
